import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef, ElementType } from 'react';

import { cn } from '@core/utils/cn';

export const surfaceVariants = cva('', {
  variants: {
    tone: {
      /** Cards and rows over a static background. The default. */
      raised: 'bg-surface',
      /** Menus and popovers sitting on top of a raised surface. */
      overlay: 'bg-overlay',
      /**
       * Only over the map or over scrolling content. Glass over a static background
       * costs GPU time and looks like nothing -- use `raised` there instead.
       */
      glass: 'glass',
      plain: 'bg-transparent',
    },
    radius: {
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      card: 'rounded-card',
      sheet: 'rounded-sheet',
      none: '',
    },
    elevation: {
      none: '',
      e1: 'shadow-e1',
      e2: 'shadow-e2',
      raised: 'shadow-raised',
      glass: 'shadow-glass',
      modal: 'shadow-modal',
    },
    bordered: {
      true: 'border border-separator',
      false: '',
    },
  },
  defaultVariants: { tone: 'raised', radius: 'card', elevation: 'e1', bordered: false },
});

export interface SurfaceProps
  extends ComponentPropsWithRef<'div'>, VariantProps<typeof surfaceVariants> {
  as?: ElementType;
}

/**
 * The one place a background, radius and shadow are chosen together.
 *
 * Concentricity is the caller's job and it is arithmetic, not taste:
 * `inner = outer - padding`. A `radius="card"` (22px) surface with `p-3` (12px) holds
 * its children at 10px, which is `radius-sm`.
 */
export function Surface({
  as: Component = 'div',
  className,
  tone,
  radius,
  elevation,
  bordered,
  ...props
}: SurfaceProps) {
  return (
    <Component
      className={cn(surfaceVariants({ tone, radius, elevation, bordered }), className)}
      {...props}
    />
  );
}
